import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { normalizeUserRole, USER_ROLE_IDS, userRoleFromID, UserRole } from '../enums/user-role.enum';
import { StatusID } from '../enums/status-id.enum';
import { User } from '../models/user.model';
import { Organisation } from '../models/organisation.model';
import { environment } from '../../environment';
import { RegistrationVerificationService } from './registration-verification.service';
import { FeatureFlagService } from './feature-flag.service';

interface LoginRequest {
  username: string;
  password: string;
}

export interface RegisterRequest {
  userName: string;
  userEmail: string;
  userPassword: string;
  userRole: UserRole;
  organisationName?: string;
  organisationWebsite?: string;
  organisationPhone?: string;
  organisationAddress?: string;
  phoneNumber?: string;
}

type AuthResponse = string | {
  token?: string;
  accessToken?: string;
  bearerToken?: string;
  authToken?: string;
  jwt?: string;
  bearer?: string;
  access_token?: string;
  user?: User;
  currentUser?: User;
  userStatusID?: number;
  userStatusId?: number;
  userStatusName?: string;
  organisationID?: number;
  organisationName?: string;
  organisationStatusID?: number;
  organisationStatusId?: number;
  organisationStatusName?: string;
  isOperatorOnboardingComplete?: boolean;
  data?: AuthResponse;
  result?: AuthResponse;
};

interface SessionUser extends User {
  organisationStatusID?: number | null;
  organisationStatusName?: string | null;
  isOperatorOnboardingComplete?: boolean | null;
}

const TOKEN_KEY = 'token';
const CURRENT_USER_KEY = 'currentUser';
const LAST_ACTIVITY_KEY = 'lastActivityAt';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private currentUserSubject: BehaviorSubject<User | null>;
  public currentUser: Observable<User | null>;
  private authUrl = `${environment.apiUrl}/Auth`;
  private userUrl = `${environment.apiUrl}/User`;
  private organisationUrl = `${environment.apiUrl}/Organisation`;

  constructor(
    private http: HttpClient,
    private registrationVerificationService: RegistrationVerificationService,
    private featureFlags: FeatureFlagService
  ) {
    this.currentUserSubject = new BehaviorSubject<User | null>(
      this.readStoredUser()
    );
    this.currentUser = this.currentUserSubject.asObservable();
  }

  public get currentUserValue(): User | null {
    return this.currentUserSubject.value;
  }

  login(username: string, password: string): Observable<User> {
    const request: LoginRequest = { username, password };

    return this.http.post<AuthResponse>(`${this.authUrl}/login`, request).pipe(
      map(response => {
        const token = this.extractToken(response);

        if (!token) {
          throw new Error('Login response did not include a bearer token.');
        }

        const user = this.enrichSessionUser(
          this.extractUser(response) || this.createSessionUser(username, token),
          response,
          token
        );
        this.assertCanLogin(user);
        this.storeSession(token, user);
        this.featureFlags.loadEffectiveFeatures(true).subscribe({ error: () => undefined });
        return this.normalizeUser(user);
      })
    );
  }

  register(data: RegisterRequest): Observable<User> {
    const role = normalizeUserRole(data.userRole);
    const organisationRequest: Observable<Organisation | null> = role === UserRole.OPERATOR && data.organisationName
      ? this.createOrganisation(data)
      : of(null);

    return organisationRequest.pipe(
      switchMap(organisation => {
        const payload = this.createUserPayload(data, role, organisation?.organisationID || null);
        return this.http.post<User>(this.userUrl, payload).pipe(
          switchMap(user => this.registrationVerificationService.requestEmailVerification(data.userEmail).pipe(
            catchError(error => {
              console.error('Email verification request failed after registration:', error);
              return of(null);
            }),
            map(() => user)
          ))
        );
      }),
      map(user => this.normalizeUser(user))
    );
  }

  logout(): void {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(CURRENT_USER_KEY);
    localStorage.removeItem(LAST_ACTIVITY_KEY);
    this.currentUserSubject.next(null);
    this.featureFlags.clearSession();
  }

  getToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  }

  getCurrentUser(): User | null {
    return this.currentUserValue;
  }

  isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token || this.isTokenExpired(token) || this.isSessionExpired()) {
      this.logout();
      return false;
    }

    return true;
  }

  hasRole(role: UserRole): boolean {
    return normalizeUserRole(this.currentUserValue?.userRole) === role;
  }

  private createOrganisation(data: RegisterRequest): Observable<Organisation> {
    const now = new Date().toISOString();

    return this.http.post<Organisation>(this.organisationUrl, {
      organisationID: 0,
      orgName: data.organisationName,
      orgRegistrationNumber: '',
      orgLogo: null,
      orgWebsite: data.organisationWebsite || null,
      orgCreateDate: now,
      orgModifyDate: now,
      statusID: StatusID.PENDING
    });
  }

  private createUserPayload(data: RegisterRequest, role: UserRole, organisationID: number | null): Partial<User> {
    const [firstName, ...lastNameParts] = data.userName.trim().split(/\s+/);
    const now = new Date().toISOString();
    const statusID = role === UserRole.OPERATOR
      ? StatusID.PENDING
      : StatusID.PENDING;

    return {
      userID: 0,
      username: data.userEmail,
      userPassword: data.userPassword,
      userPrefix: null,
      userFirstName: firstName || data.userName,
      userLastName: lastNameParts.join(' '),
      userCreateDate: now,
      userModifyDate: now,
      userRoleID: USER_ROLE_IDS[role],
      organisationID,
      organisation: null,
      addressID: null,
      contactID: null,
      statusID
    };
  }

  private storeSession(token: string, user: User): void {
    const normalizedUser = this.normalizeUser(user);
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(CURRENT_USER_KEY, JSON.stringify(normalizedUser));
    this.touchSession();
    this.currentUserSubject.next(normalizedUser);
  }

  private readStoredUser(): User | null {
    const token = this.getToken();
    if (!token || this.isTokenExpired(token) || this.isSessionExpired()) {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(CURRENT_USER_KEY);
      localStorage.removeItem(LAST_ACTIVITY_KEY);
      return null;
    }

    const storedUser = localStorage.getItem(CURRENT_USER_KEY);

    if (!storedUser) {
      return null;
    }

    try {
      return this.normalizeUser(JSON.parse(storedUser));
    } catch {
      localStorage.removeItem(CURRENT_USER_KEY);
      return null;
    }
  }

  touchSession(): void {
    if (this.getToken()) {
      localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
    }
  }

  isSessionExpired(): boolean {
    const lastActivity = Number(localStorage.getItem(LAST_ACTIVITY_KEY) || 0);
    if (!lastActivity) {
      return false;
    }

    return Date.now() - lastActivity > this.getSessionTimeoutMs();
  }

  private getSessionTimeoutMs(): number {
    return (environment.sessionTimeoutMinutes || 30) * 60 * 1000;
  }

  private isTokenExpired(token: string): boolean {
    const payload = this.decodeTokenPayload(token);
    if (!payload?.exp) {
      return false;
    }

    return Date.now() >= Number(payload.exp) * 1000;
  }

  private extractToken(response: AuthResponse): string | undefined {
    if (!response) {
      return undefined;
    }

    if (typeof response === 'string') {
      return response;
    }

    return response.token
      || response.accessToken
      || response.bearerToken
      || response.authToken
      || response.jwt
      || response.bearer
      || response.access_token
      || (response.data ? this.extractToken(response.data) : undefined)
      || (response.result ? this.extractToken(response.result) : undefined);
  }

  private extractUser(response: AuthResponse): User | undefined {
    if (!response || typeof response === 'string') {
      return undefined;
    }

    return response.user
      || response.currentUser
      || (response.data ? this.extractUser(response.data) : undefined)
      || (response.result ? this.extractUser(response.result) : undefined);
  }

  private createSessionUser(username: string, token: string): SessionUser {
    const tokenUser = this.decodeTokenPayload(token);

    return {
      userID: Number(tokenUser?.sub || tokenUser?.userID || tokenUser?.nameid || 0),
      username,
      userName: tokenUser?.name || tokenUser?.unique_name || username,
      userEmail: tokenUser?.email || username,
      userRole: normalizeUserRole(tokenUser?.role || tokenUser?.userRole || tokenUser?.userRoleName || tokenUser?.userRoleID),
      organisationID: tokenUser?.organisationID ? Number(tokenUser.organisationID) : null,
      statusID: this.toOptionalNumber(tokenUser?.userStatusId || tokenUser?.userStatusID || tokenUser?.statusID) ?? undefined,
      organisationStatusID: this.toOptionalNumber(tokenUser?.organisationStatusId || tokenUser?.organisationStatusID),
      isOperatorOnboardingComplete: this.toOptionalBoolean(tokenUser?.isOperatorOnboardingComplete),
      organisation: null
    };
  }

  private decodeTokenPayload(token: string): any | null {
    const parts = token.split('.');

    if (parts.length < 2) {
      return null;
    }

    try {
      const normalized = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
      return JSON.parse(atob(padded));
    } catch {
      return null;
    }
  }

  private normalizeUser(user: SessionUser): SessionUser {
    const role = user.userRoleName
      ? normalizeUserRole(user.userRoleName)
      : user.userRole
        ? normalizeUserRole(user.userRole)
        : user.userRoleID
          ? userRoleFromID(user.userRoleID)
          : UserRole.PASSENGER;

    return {
      ...user,
      userName: user.userName || user.username || [user.userFirstName, user.userLastName].filter(Boolean).join(' ') || 'User',
      userRole: role,
      userRoleID: user.userRoleID || USER_ROLE_IDS[role],
      statusID: user.statusID,
      organisationStatusID: user.organisationStatusID ?? this.toOptionalNumber(user.organisation?.statusID),
      organisationStatusName: user.organisationStatusName,
      isOperatorOnboardingComplete: user.isOperatorOnboardingComplete
    };
  }

  private assertCanLogin(user: SessionUser): void {
    const role = normalizeUserRole(user.userRole);
    const userStatusID = this.toOptionalNumber(user.statusID);
    const organisationStatusID = this.toOptionalNumber(user.organisationStatusID ?? user.organisation?.statusID);

    if (role === UserRole.OPERATOR && userStatusID !== null && userStatusID !== StatusID.ACTIVE) {
      throw new Error('Your operator account is not active yet. Riya Shuma must complete onboarding and activate the account before login.');
    }

    if (role === UserRole.OPERATOR && organisationStatusID !== null && organisationStatusID !== StatusID.ACTIVE) {
      throw new Error('Your operator organisation is not active yet. Riya Shuma must complete onboarding and activate the account before login.');
    }

    if (role !== UserRole.OPERATOR && userStatusID === StatusID.PENDING) {
      throw new Error('Your account is pending verification or approval. Please check your verification message or contact Riya Shuma support.');
    }

    if (userStatusID === StatusID.INACTIVE || userStatusID === StatusID.SUSPENDED) {
      throw new Error('Your account is not active. Please contact Riya Shuma support.');
    }
  }

  private enrichSessionUser(user: User, response: AuthResponse, token: string): SessionUser {
    const tokenUser = this.decodeTokenPayload(token);
    const authResponse = this.unwrapAuthResponse(response);
    const sessionUser: SessionUser = { ...user };

    const responseUserStatusID = this.toOptionalNumber(authResponse?.userStatusID ?? authResponse?.userStatusId);
    const tokenUserStatusID = this.toOptionalNumber(tokenUser?.userStatusId ?? tokenUser?.userStatusID);
    sessionUser.statusID = responseUserStatusID ?? tokenUserStatusID ?? sessionUser.statusID;
    sessionUser.statusName = authResponse?.userStatusName ?? sessionUser.statusName;

    const responseOrganisationID = this.toOptionalNumber(authResponse?.organisationID);
    sessionUser.organisationID = responseOrganisationID ?? sessionUser.organisationID ?? null;
    sessionUser.organisationName = authResponse?.organisationName ?? sessionUser.organisationName;

    sessionUser.organisationStatusID = this.toOptionalNumber(
      authResponse?.organisationStatusID
      ?? authResponse?.organisationStatusId
      ?? tokenUser?.organisationStatusId
      ?? tokenUser?.organisationStatusID
    );
    sessionUser.organisationStatusName = authResponse?.organisationStatusName ?? sessionUser.organisationStatusName;
    sessionUser.isOperatorOnboardingComplete = this.toOptionalBoolean(
      authResponse?.isOperatorOnboardingComplete
      ?? tokenUser?.isOperatorOnboardingComplete
    );

    if (!sessionUser.organisation && (sessionUser.organisationID || sessionUser.organisationName || sessionUser.organisationStatusID)) {
      sessionUser.organisation = {
        organisationID: sessionUser.organisationID || 0,
        orgName: sessionUser.organisationName || null,
        statusID: sessionUser.organisationStatusID ?? null
      };
    }

    return sessionUser;
  }

  private unwrapAuthResponse(response: AuthResponse): any {
    if (!response || typeof response === 'string') {
      return null;
    }

    if (response.data) {
      return this.unwrapAuthResponse(response.data);
    }

    if (response.result) {
      return this.unwrapAuthResponse(response.result);
    }

    return response;
  }

  private toOptionalNumber(value: any): number | null {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
  }

  private toOptionalBoolean(value: any): boolean | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    if (typeof value === 'boolean') {
      return value;
    }

    return String(value).toLowerCase() === 'true';
  }
}
